import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/ui/button";
import type { ReactNode } from "react";
import { Maximize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEscapeScope } from "@/components/ui/escape-scope";
import { tabStops, wrapTarget } from "@/components/ui/tab-ring";
import { FOCUS_RING, PANEL_CONTROL } from "./panel-styles";

/** How long the way out stays on screen before it stops being worth the space. */
const HINT_MS = 5000;

/**
 * Whether this drawing is filling the screen, and the ways in and out of that.
 *
 * Called from inside the branch that is keyed by the drawing, so moving to another one
 * takes this with it and the next drawing opens at panel size. Every way out goes through
 * `leave`, so the keyboard lands back on the control that opened full screen however it
 * was left: the toggle, the title bar's close, Escape.
 *
 * View state only. Nothing here is written down, so entering and leaving issue nothing.
 */
export function useFullscreenFigure() {
  const [fullscreen, setFullscreen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // The way in is off the screen while the panel is up, so the keyboard cannot be handed
  // back to it until the render that brings it back. Marked on the way out and spent on
  // the way in, rather than focusing something that is not there yet.
  const returningRef = useRef(false);

  const leave = useCallback(() => {
    returningRef.current = true;
    setFullscreen(false);
  }, []);

  useEffect(() => {
    if (fullscreen || !returningRef.current) return;
    returningRef.current = false;
    toggleRef.current?.focus();
  }, [fullscreen]);

  const open = useCallback(() => {
    setFullscreen(true);
  }, []);

  return { fullscreen, open, leave, toggleRef };
}

/**
 * The way in to full size, over the drawing it expands.
 *
 * Only the way in. Once the panel is up the design takes this control off the screen and
 * the title bar's close is the visible way out, with Escape beside it and the hint saying
 * so. A second exit sitting over the drawing would compete with the one in the chrome and
 * would put a control in the tab ring that repeats what is already there.
 *
 * Two words on the button and the long wording in its tooltip, where the design puts it:
 * a sentence printed on a control that sits over artwork spends the drawing's own space
 * explaining a button, and reads as a description rather than a label.
 *
 * Backplated because it sits over artwork whose colour it cannot know, and rendered
 * outside the layer that reads clicks as annotation, so a press here is never also a
 * click on the canvas.
 */
export function FullscreenToggle({
  onOpen,
  ref,
}: {
  onOpen: () => void;
  /** So whatever leaves full screen can hand the keyboard back here. */
  ref?: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <button
      ref={ref}
      data-testid="define-fullscreen-toggle"
      type="button"
      onClick={onOpen}
      title="Edit numerals and connectors at full size"
      className={cn(
        PANEL_CONTROL,
        FOCUS_RING,
        "absolute right-2 top-2 bg-card/90 px-2 py-1 text-muted-foreground shadow-sm hover:text-foreground",
      )}
    >
      <Maximize2 className="size-3.5" aria-hidden />
      Full screen
    </button>
  );
}

/**
 * Keep the keyboard inside the panel while it covers the page.
 *
 * The panel cannot seal the page by marking it inert: it renders inside the very content
 * it needs to shut off, and inertness is inherited, so an ancestor cannot be inert while
 * this is not. Moving it out to a portal would seal it, and would also remount the card
 * and lose a half-made placement, which is the one thing this feature must not do. So the
 * key is caught instead: Tab off either end comes back round to the other.
 *
 * The pointer is already handled, by the backdrop that covers everything else, and
 * assistive technology by `aria-modal`. This is the third way out of the panel and the
 * only one left open.
 */
function useKeyboardHeld(
  open: boolean,
  panel: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return undefined;
    function held(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const inside = panel.current;
      if (inside === null) return;
      const stops = tabStops(inside);
      if (stops.length === 0) return;
      const on = document.activeElement;
      const to =
        on === null || !inside.contains(on)
          ? stops[0]
          : wrapTarget(stops, on, event.shiftKey);
      if (!to) return;
      event.preventDefault();
      to.focus();
    }
    document.addEventListener("keydown", held);
    return () => {
      document.removeEventListener("keydown", held);
    };
  }, [open, panel]);
}

/**
 * The way out, said once on arrival.
 *
 * Full screen has no window chrome of its own, so the key that leaves it is the one thing
 * the attorney cannot see. It says so on entry and then gets out of the way, because a
 * banner that stays is a banner that is being read past by the second drawing.
 *
 * Positioned against the panel rather than against what scrolls inside it, so it stays
 * where it was put for the seconds it is up.
 */
function ExitHint() {
  const [saying, setSaying] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSaying(false);
    }, HINT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!saying) return null;
  return (
    <p
      role="status"
      className="animate-fade-in-up pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-foreground/85 px-3 py-1.5 text-[11.5px] font-medium text-background shadow-lg"
    >
      Press{" "}
      <kbd className="rounded border border-background/40 px-1 font-mono text-[10.5px]">
        Esc
      </kbd>{" "}
      to exit full-screen mode
    </p>
  );
}

/**
 * The annotation surface at the size of the viewport, expanded in place.
 *
 * The children stay mounted in both states and only their classes change. Wrapping them
 * when open, or moving them into a portal, would put them somewhere else in the tree;
 * React would unmount what was there and mount it again, and a placement waiting for its
 * second click would be gone. Expanding is meant to be a change of size, so for anything
 * holding state it is only ever a change of size. The chrome around them holds nothing,
 * so it is simply there or not.
 *
 * The panel is the one thing here anchored to the viewport. Anything else that needs to
 * float at full size renders inside it, so the question of which ancestor a fixed box
 * actually hangs off is asked once about one element.
 *
 * Escape leaves, but only when nothing nearer wants it: a popover shuts first, then a
 * placement or a drag is called off, and full screen is what is left when there is nothing
 * else to abandon. That order is the scope stack's, not this component's.
 */
export function FullscreenPanel({
  open,
  heading,
  filename,
  onExit,
  children,
}: {
  open: boolean;
  /** The figure's name, so the panel says which drawing is filling the screen. */
  heading: string;
  filename: string;
  onExit: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEscapeScope("fullscreen", open, onExit);
  useKeyboardHeld(open, panelRef);

  return (
    <>
      {open && (
        <div aria-hidden className="fixed inset-0 z-40 bg-foreground/40" />
      )}
      <div
        ref={panelRef}
        data-testid="define-fullscreen"
        data-open={open}
        // Announced as what it visibly is once it covers the page. Off in the panel
        // layout, where it is one column among several and claiming a dialog would be a
        // lie the reader has to work around.
        role={open ? "dialog" : undefined}
        aria-modal={open ? true : undefined}
        aria-label={open ? `${heading} at full size` : undefined}
        className={cn(
          open &&
            "fixed inset-[18px] z-50 flex flex-col overflow-hidden rounded-lg border border-border bg-card p-3.5 shadow-lg",
        )}
      >
        {open && (
          <div className="z-10 mb-2.5 flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border bg-card pb-2">
            <h3 className="text-sm font-semibold tracking-tight">{heading}</h3>
            <span className="font-mono text-[11.5px] text-muted-foreground">
              {filename}
            </span>
            <IconButton
              variant="quiet"
              size="dense"
              type="button"
              onClick={onExit}
              title="Exit full screen (Esc)"
              aria-label="Exit full screen"
              className={cn(FOCUS_RING, "ml-auto")}
            >
              <X className="size-4" aria-hidden />
            </IconButton>
          </div>
        )}
        {/* Nothing here scrolls. The room left under the title bar is handed to what is
            inside, and the one thing that can outgrow it, the drawing, scrolls within its
            own frame. So the controls, the frame's border and the registry all stay where
            the attorney last saw them however far into a tall drawing they are. */}
        <div
          data-testid="define-fullscreen-room"
          className={cn(open && "min-h-0 flex-1 overflow-hidden")}
        >
          {children}
        </div>
        {/* Keyed by nothing and mounted only while open, so each entry says it again and
            the timer that hides it dies with the panel rather than outliving it. */}
        {open && <ExitHint />}
      </div>
    </>
  );
}
